// Utility functions for common operations
/**
 * Delays execution for specified milliseconds
 */
export const sleep = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};
/**
 * Generates a random UUID v4
 */
export const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};
/**
 * Capitalizes the first letter of a string
 */
export const capitalize = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};
/**
 * Converts a string to kebab-case
 */
export const toKebabCase = (str) => {
    return str
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/[\s_]+/g, '-')
        .toLowerCase();
};
/**
 * Converts a string to camelCase
 */
export const toCamelCase = (str) => {
    return str
        .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
        .replace(/[\s-_]+/g, '');
};
/**
 * Safely parses JSON with fallback
 */
export const safeJsonParse = (json, fallback) => {
    try {
        return JSON.parse(json);
    }
    catch {
        return fallback;
    }
};
/**
 * Removes undefined values from an object
 */
export const removeUndefined = (obj) => {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
            result[key] = value;
        }
    }
    return result;
};
/**
 * Picks specific keys from an object
 */
export const pick = (obj, keys) => {
    const result = {};
    for (const key of keys) {
        if (key in obj) {
            result[key] = obj[key];
        }
    }
    return result;
};
/**
 * Omits specific keys from an object
 */
export const omit = (obj, keys) => {
    const result = { ...obj };
    for (const key of keys) {
        delete result[key];
    }
    return result;
};
/**
 * Validates email format
 */
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};
// ==================== VALIDATION CONSTANTS ====================
/**
 * Routing number validation regex - exactly 9 digits, allows leading zeros
 */
export const ROUTING_NUMBER_REGEX = /^\d{9}$/;
/**
 * Account number validation regex - 4-17 digits
 */
export const ACCOUNT_NUMBER_REGEX = /^\d{4,17}$/;
/**
 * Validates that a routing number is exactly 9 digits.
 * This is the basic format validation - checks length and numeric characters only.
 *
 * @param routingNumber - The routing number to validate
 * @returns true if the routing number is exactly 9 digits
 *
 * @example
 * isValidRoutingNumberFormat('123456789') // true
 * isValidRoutingNumberFormat('000123456') // true (leading zeros allowed)
 * isValidRoutingNumberFormat('12345678')  // false (8 digits)
 * isValidRoutingNumberFormat('1234567890') // false (10 digits)
 * isValidRoutingNumberFormat('12345678a') // false (contains letter)
 */
export const isValidRoutingNumberFormat = (routingNumber) => {
    if (!routingNumber || typeof routingNumber !== 'string') {
        return false;
    }
    return ROUTING_NUMBER_REGEX.test(routingNumber);
};
/**
 * Validates a routing number using the ABA check digit algorithm.
 * This validates both the format (9 digits) and the checksum.
 *
 * The ABA routing number checksum algorithm:
 * - Multiply each digit by a weight: 3, 7, 1, 3, 7, 1, 3, 7, 1
 * - Sum all the products
 * - The sum must be divisible by 10
 *
 * @param routingNumber - The routing number to validate
 * @returns true if the routing number has a valid checksum
 *
 * @example
 * isValidRoutingNumberChecksum('021000021') // true (Chase Bank)
 * isValidRoutingNumberChecksum('123456789') // false (invalid checksum)
 */
export const isValidRoutingNumberChecksum = (routingNumber) => {
    if (!isValidRoutingNumberFormat(routingNumber)) {
        return false;
    }
    const digits = routingNumber.split('').map(Number);
    const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
    let sum = 0;
    for (let i = 0; i < 9; i++) {
        sum += digits[i] * weights[i];
    }
    return sum % 10 === 0;
};
/**
 * Validates a routing number with detailed error information.
 * Checks format only (9 digits) - does not validate checksum.
 *
 * @param routingNumber - The routing number to validate
 * @returns Validation result with isValid flag and error details
 *
 * @example
 * validateRoutingNumber('123456789')
 * // { isValid: true }
 *
 * validateRoutingNumber('')
 * // { isValid: false, error: 'empty', message: 'Routing number is required' }
 *
 * validateRoutingNumber('12345678')
 * // { isValid: false, error: 'too_short', message: 'Routing number must be exactly 9 digits (currently 8 digits)' }
 */
export const validateRoutingNumber = (routingNumber) => {
    // Handle null, undefined, or empty string
    if (!routingNumber || routingNumber.trim() === '') {
        return {
            isValid: false,
            error: 'empty',
            message: 'Routing number is required',
        };
    }
    const trimmed = routingNumber.trim();
    // Check for non-numeric characters
    if (!/^\d+$/.test(trimmed)) {
        return {
            isValid: false,
            error: 'non_numeric',
            message: 'Routing number must contain only digits (0-9)',
        };
    }
    // Check length
    if (trimmed.length < 9) {
        return {
            isValid: false,
            error: 'too_short',
            message: `Routing number must be exactly 9 digits (currently ${trimmed.length} digit${trimmed.length === 1 ? '' : 's'})`,
        };
    }
    if (trimmed.length > 9) {
        return {
            isValid: false,
            error: 'too_long',
            message: `Routing number must be exactly 9 digits (currently ${trimmed.length} digits)`,
        };
    }
    return { isValid: true };
};
/**
 * Validates a routing number with checksum validation and detailed error information.
 *
 * @param routingNumber - The routing number to validate
 * @returns Validation result with isValid flag and error details
 */
export const validateRoutingNumberWithChecksum = (routingNumber) => {
    // First do basic format validation
    const formatResult = validateRoutingNumber(routingNumber);
    if (!formatResult.isValid) {
        return formatResult;
    }
    // Then validate checksum
    if (!isValidRoutingNumberChecksum(routingNumber)) {
        return {
            isValid: false,
            error: 'invalid_checksum',
            message: 'Routing number is invalid',
        };
    }
    return { isValid: true };
};
/**
 * Gets a user-friendly error message for routing number validation.
 * Includes ABA checksum validation.
 * Useful for displaying in UI form fields.
 *
 * @param routingNumber - The routing number to validate
 * @returns Error message string, or null if valid
 */
export const getRoutingNumberError = (routingNumber) => {
    const result = validateRoutingNumberWithChecksum(routingNumber);
    return result.isValid ? null : (result.message ?? 'Invalid routing number');
};
/**
 * Checks if a string is in scientific notation format (e.g., 1.23e+10, 1E10, 2.5e-5)
 *
 * @param value - The string to check
 * @returns true if the string appears to be in scientific notation
 *
 * @example
 * isScientificNotation('1.23e+10') // true
 * isScientificNotation('1E10') // true
 * isScientificNotation('2.5e-5') // true
 * isScientificNotation('1234567890') // false
 */
export const isScientificNotation = (value) => {
    if (!value || typeof value !== 'string') {
        return false;
    }
    // Match patterns like: 1e10, 1E10, 1.23e+10, 2.5E-5, etc.
    return /^[+-]?\d*\.?\d+[eE][+-]?\d+$/.test(value.trim());
};
/**
 * Validates that an account number has valid format (4-17 digits, no scientific notation).
 *
 * @param accountNumber - The account number to validate
 * @returns true if the account number format is valid (4-17 digits)
 *
 * @example
 * isValidAccountNumberFormat('1234567890') // true (format only)
 * isValidAccountNumberFormat('1234') // true (minimum 4 digits)
 * isValidAccountNumberFormat('123') // false (too short)
 * isValidAccountNumberFormat('1.23e+10') // false (scientific notation)
 */
export const isValidAccountNumberFormat = (accountNumber) => {
    if (!accountNumber || typeof accountNumber !== 'string') {
        return false;
    }
    // Check for scientific notation first
    if (isScientificNotation(accountNumber)) {
        return false;
    }
    return ACCOUNT_NUMBER_REGEX.test(accountNumber);
};
/**
 * Validates an account number format only
 *
 * @param accountNumber - The account number to validate
 * @returns Validation result with isValid flag and error details
 */
export const validateAccountNumberFormat = (accountNumber) => {
    // Handle null, undefined, or empty string
    if (!accountNumber || accountNumber.trim() === '') {
        return {
            isValid: false,
            error: 'empty',
            message: 'Account number is required',
        };
    }
    const trimmed = accountNumber.trim();
    // Check for scientific notation FIRST (before numeric check)
    if (isScientificNotation(trimmed)) {
        return {
            isValid: false,
            error: 'scientific_notation',
            message: 'Account number cannot be in scientific notation format (e.g., 1.23e+10)',
        };
    }
    // Check for non-numeric characters
    if (!/^\d+$/.test(trimmed)) {
        return {
            isValid: false,
            error: 'non_numeric',
            message: 'Account number must contain only digits (0-9)',
        };
    }
    // Check minimum length
    if (trimmed.length < 4) {
        return {
            isValid: false,
            error: 'too_short',
            message: `Account number must be at least 4 digits (currently ${trimmed.length} digit${trimmed.length === 1 ? '' : 's'})`,
        };
    }
    // Check maximum length
    if (trimmed.length > 17) {
        return {
            isValid: false,
            error: 'too_long',
            message: `Account number cannot exceed 17 digits (currently ${trimmed.length} digits)`,
        };
    }
    return { isValid: true };
};
/**
 * Gets a user-friendly error message for account number validation.
 * Validates format only (4-17 digits, no scientific notation).
 * Useful for displaying in UI form fields.
 *
 * @param accountNumber - The account number to validate
 * @returns Error message string, or null if valid
 */
export const getAccountNumberError = (accountNumber) => {
    const result = validateAccountNumberFormat(accountNumber);
    return result.isValid ? null : (result.message ?? 'Invalid account number');
};
// ==================== PHONE NUMBER VALIDATION ====================
/**
 * E.164-compatible phone number regex.
 * Optional leading +, must start with a non-zero digit, 2-15 digits total.
 */
export const PHONE_NUMBER_REGEX = /^\+?[1-9]\d{1,14}$/;
/**
 * Normalizes a raw phone number input by stripping whitespace, hyphens, dots, and parentheses.
 */
export const normalizePhoneNumber = (raw) => raw.replace(/[\s\-().]/g, '');
/**
 * Validates a phone number with detailed error information.
 *
 * @param phoneNumber - Raw user input (spaces/hyphens are stripped before testing)
 * @param options.required - Whether an empty value is an error (default true)
 * @returns Validation result with isValid flag and optional error details
 *
 * @example
 * validatePhoneNumber('+1 234 567 8900')  // { isValid: true }
 * validatePhoneNumber('12345678900')       // { isValid: true }
 * validatePhoneNumber('')                  // { isValid: false, error: 'empty', ... }
 * validatePhoneNumber('abc')              // { isValid: false, error: 'invalid_format', ... }
 * validatePhoneNumber('', { required: false }) // { isValid: true }
 */
export const validatePhoneNumber = (phoneNumber, options = {}) => {
    const { required = true } = options;
    if (!phoneNumber || phoneNumber.trim() === '') {
        if (!required)
            return { isValid: true };
        return {
            isValid: false,
            error: 'empty',
            message: 'Phone number is required',
        };
    }
    const normalized = normalizePhoneNumber(phoneNumber);
    if (!PHONE_NUMBER_REGEX.test(normalized)) {
        return {
            isValid: false,
            error: 'invalid_format',
            message: 'Invalid phone number format',
        };
    }
    return { isValid: true };
};
/**
 * Gets a user-friendly error message for phone number validation.
 * Useful for displaying in UI form fields.
 *
 * @param phoneNumber - The raw phone input to validate
 * @param options.required - Whether an empty value is an error (default true)
 * @returns Error message string, or null if valid
 */
export const getPhoneNumberError = (phoneNumber, options = {}) => {
    const result = validatePhoneNumber(phoneNumber, options);
    return result.isValid ? null : (result.message ?? 'Invalid phone number');
};
/**
 * Validates password strength
 */
export const isValidPassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
};
/**
 * Formats file size in human readable format
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0)
        return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};
/**
 * Debounces a function call
 */
export const debounce = (func, wait) => {
    let timeout = null;
    return (...args) => {
        if (timeout)
            clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};
/**
 * Throttles a function call
 */
export const throttle = (func, limit) => {
    let inThrottle;
    return (...args) => {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => {
                inThrottle = false;
            }, limit);
        }
    };
};
export * from './date.util.js';
// Ordering for columns whose cell shows a label looked up from an ID
export * from './display-order.util.js';
// Printable-ASCII, payment date and payment amount checks for file fields
export * from './field-value.util.js';
export * from './parse-prefixed-search.js';
export * from './payment-released-unconfirmed-alert.util.js';
export * from './payment-return.util.js';
export * from './payment-returned-alert.util.js';
// SFTP login name helpers (Transfer Family charset, group paths)
export * from './sftp-username.util.js';
// SSH public key validation (SFTP key-based auth)
export * from './ssh-public-key.util.js';
// User type utilities
export * from './user-type.utils.js';
//# sourceMappingURL=index.js.map